import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdArrowBack,
  MdCheckCircle,
  MdEdit,
  MdPayment,
  MdShoppingCart,
} from 'react-icons/md';
import {
  erpTableClass,
  erpTableHeadCellClass,
  erpTableHeadRowClass,
  erpTableRowClasses,
  erpTableScrollClass,
  erpTableShellClass,
  EUROASIANN_OFFER_LABEL,
  formatServerDate,
} from '@euroasiann/shared';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../components/shared/Toast';
import {
  ProcurementWorkflowStepper,
  resolveProcurementWorkflowStatus,
  type ProcurementWorkflowContext,
} from '../../components/procurement';
import { RfqDeliveryStatusSection } from '../../components/orders/RfqDeliveryStatusSection';
import { AdminBuyerInvoicesSection } from '../../components/orders/AdminBuyerInvoicesSection';
import { AdminBuyerShippingSection } from '../../components/orders/AdminBuyerShippingSection';
import { AdminBuyerPaymentSection } from '../../components/orders/AdminBuyerPaymentSection';
import { toDisplayStatus, getRfqStatusBadgeClasses, resolveStatusFilterBucket } from '../../utils/rfq-status.utils';
import type { Shipment } from '../../types/shipment.types';
import { PageSkeleton } from '@euroasiann/shared/ui';

interface RFQDetails {
  _id: string;
  rfqNumber: string;
  title: string;
  description?: string;
  vesselId?: {
    _id: string;
    name: string;
    imoNumber?: string;
    hullNumber?: string;
    serialNumber?: string;
    type?: string;
  };
  supplyPort?: string;
  brand?: string;
  model?: string;
  category?: string;
  subCategory?: string;
  equipmentTags?: string;
  drawingNumber?: string;
  preferredQuality?: string;
  leadDate?: string;
  incoterms?: string;
  logisticContainer?: string;
  status: string;
  dueDate?: string;
  createdAt: string;
  updatedAt?: string;
  senderId?: { _id: string; name: string; email?: string };
  recipientVendorIds?: Array<{
    _id: string;
    name: string;
    type?: string;
    portalType?: string;
  }>;
  metadata?: Record<string, any>;
}

interface VendorQuotation {
  _id: string;
  quotationNumber: string;
  vendorOrganizationId?: {
    _id: string | null;
    name: string;
    email?: string;
  };
  isAdminOffer?: boolean;
  status: string;
  submittedAt?: string;
  createdAt?: string;
  totalAmount?: number;
  currency?: string;
  items: Array<{
    description: string;
    itemDescription?: string;
    requiredQty: number;
    quotedPrice: number;
    unitPrice?: number;
    offeredQty: number;
    offeredQuality: string;
    discountPercent?: number;
    taxPercent?: number;
  }>;
  selectedItemIndexes?: number[];
  terms?: {
    creditType?: string;
    paymentTerm?: string;
    insuranceTerm?: string;
    taxTerm?: string;
    transportTerm?: string;
    deliveryTerm?: string;
    packingTerm?: string;
    currency?: string;
  };
}

function statusBadge(status: string) {
  return getRfqStatusBadgeClasses(status);
}

export function YourRFQDetailsPage({
  rfqId: rfqIdProp,
  backTo = '/dashboard/admin/purchases?purchasesTab=rfqs',
  embedded = false,
}: {
  rfqId?: string;
  backTo?: string;
  embedded?: boolean;
} = {}) {
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = rfqIdProp || idFromParams;
  const navigate = useNavigate();
  const goBack = () => navigate(backTo);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [selectedVendorTab, setSelectedVendorTab] = useState(0);
  const [selectedItemsByQuotation, setSelectedItemsByQuotation] = useState<
    Record<string, number[]>
  >({});

  const {
    data: rfqDetails,
    isLoading,
    error,
  } = useQuery<RFQDetails>({
    queryKey: ['admin-your-rfq-details', id],
    queryFn: async () => {
      if (!id) throw new Error('RFQ ID is required');
      const response = await authenticatedFetch(`/api/v1/admin/rfq/${id}`);
      if (!response.ok) throw new Error('Failed to fetch RFQ details');
      const data = await response.json();
      return data.data;
    },
    enabled: !!id,
  });

  const { data: vendorQuotations = [] } = useQuery<VendorQuotation[]>({
    queryKey: ['admin-your-rfq-quotations', id],
    queryFn: async () => {
      if (!id) return [];
      try {
        const response = await authenticatedFetch(`/api/v1/admin/quotation/rfq/${id}`);
        if (!response.ok) return [];
        const data = await response.json();
        const list = Array.isArray(data.data) ? data.data : [];
        return list.filter((q: VendorQuotation) => !q.isAdminOffer);
      } catch {
        return [];
      }
    },
    enabled: !!id,
  });

  const hasAnyFinalizedOffer = useMemo(() => {
    if (['finalized', 'ordered', 'awarded', 'delivered'].includes((rfqDetails?.status || '').toLowerCase())) {
      return true;
    }
    return vendorQuotations.some((q) => q.status === 'finalized');
  }, [vendorQuotations, rfqDetails?.status]);

  const { data: purchaseOrder } = useQuery<any>({
    queryKey: ['admin-your-rfq-purchase-order', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await authenticatedFetch(`/api/v1/admin/rfq/${id}/purchase-order`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.data;
      } catch {
        return null;
      }
    },
    enabled: !!id && hasAnyFinalizedOffer,
  });

  const poId = purchaseOrder?._id;

  // Enrich PO with full details (deliveryDetails) when available
  const { data: enrichedPo } = useQuery<any>({
    queryKey: ['admin-your-rfq-po-enriched', poId],
    queryFn: async () => {
      if (!poId) return null;
      const response = await authenticatedFetch(`/api/v1/admin/pos/${poId}`);
      if (!response.ok) return purchaseOrder;
      const data = await response.json();
      return data.data || purchaseOrder;
    },
    enabled: !!poId,
  });

  const po = enrichedPo || purchaseOrder;

  const { data: linkedShipment } = useQuery<Shipment | null>({
    queryKey: ['admin-your-rfq-shipment', po?.poNumber],
    queryFn: async () => {
      if (!po?.poNumber) return null;
      const response = await authenticatedFetch(
        `/api/v1/admin/shipments?search=${encodeURIComponent(po.poNumber)}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const list: Shipment[] = data.data?.shipments || [];
      return (
        list.find((s) => s.orderNumber === po.poNumber) || list[0] || null
      );
    },
    enabled: !!po?.poNumber,
  });

  const procurementWorkflowContext = useMemo((): ProcurementWorkflowContext => {
    const metadata = rfqDetails?.metadata as Record<string, unknown> | undefined;
    return {
      workflowStatus:
        typeof metadata?.procurementWorkflowStatus === 'string'
          ? metadata.procurementWorkflowStatus
          : null,
      rfqStatus: rfqDetails?.status,
      rfqMetadata: metadata,
      hasQuotations: vendorQuotations.length > 0,
      hasFinalizedQuotation: hasAnyFinalizedOffer,
      purchaseOrderId: po?._id ? String(po._id) : null,
      purchaseOrderStatus: po?.status,
      deliveryWorkflowStatus: po?.deliveryDetails?.workflowStatus,
    };
  }, [rfqDetails, vendorQuotations, hasAnyFinalizedOffer, po]);

  const procurementWorkflowStatus = useMemo(
    () => resolveProcurementWorkflowStatus(procurementWorkflowContext),
    [procurementWorkflowContext]
  );

  const rfqItems = useMemo(() => {
    const items = rfqDetails?.metadata?.items || [];
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => ({
      impaNo: item.impaNo || '-',
      description: item.itemDescription || item.description || '-',
      partNo: item.partNo || '-',
      altPartNo: item.altPartNo || '-',
      positionNo: item.positionNo || '-',
      dimensions: item.dimensions || '-',
      requiredQty: item.requiredQuantity ?? item.requiredQty ?? '-',
      uom: item.uom || '-',
      remark: item.generalRemark || item.remark || '-',
      deliveryPriority: item.deliveryPriority,
      expectedDeliveryDate: item.expectedDeliveryDate,
    }));
  }, [rfqDetails]);

  const awardMutation = useMutation({
    mutationFn: async ({
      quotationId,
      selectedItemIndexes,
    }: {
      quotationId: string;
      selectedItemIndexes?: number[];
    }) => {
      const response = await authenticatedFetch(
        `/api/v1/admin/quotation/${quotationId}/award`,
        {
          method: 'POST',
          body: JSON.stringify({
            selection: selectedItemIndexes
              ? { selectedItemIndexes }
              : undefined,
          }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to award quotation');
      }
      return data;
    },
    onSuccess: () => {
      showToast('Quotation awarded and purchase order generated', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-details', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-quotations', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-your-rfq-purchase-order', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-your-rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const showDeliveryStatus =
    hasAnyFinalizedOffer &&
    po &&
    !['draft', 'issued', 'cancelled'].includes(String(po.status || '').toLowerCase());

  const isOngoingOrProcessed =
    ['ordered', 'finalized', 'awarded', 'delivered', 'shipped', 'completed'].includes(
      (rfqDetails?.status || '').toLowerCase()
    ) ||
    hasAnyFinalizedOffer ||
    Boolean(po);

  if (isLoading) {
    return <PageSkeleton variant="cards" />;
  }

  if (error || !rfqDetails) {
    return (
      <div className="space-y-4 p-6">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-2 text-sm font-semibold text-[#0088cc]"
        >
          <MdArrowBack className="h-4 w-4" /> Back to Purchases
        </button>
        <p className="text-sm font-semibold text-rose-600">
          {(error as Error)?.message || 'RFQ not found'}
        </p>
      </div>
    );
  }

  const isDraft = resolveStatusFilterBucket(rfqDetails.status) === 'Draft';
  const vesselInfo = rfqDetails.vesselId;

  return (
    <div className={cn('w-full space-y-6', embedded ? 'pb-4' : 'pb-12')}>
      <button
        type="button"
        onClick={goBack}
        className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0088cc]/65 transition-colors hover:text-[#0088cc]"
      >
        ← Purchases RFQs
      </button>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0088cc]">
            Directive Details
          </span>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-[hsl(var(--foreground))]">
              {rfqDetails.title || rfqDetails.rfqNumber}
            </h1>
            <span
              className={cn(
                'rounded-full border px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest',
                statusBadge(rfqDetails.status)
              )}
            >
              {toDisplayStatus(rfqDetails.status)}
            </span>
          </div>
          <p className="mt-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            {rfqDetails.rfqNumber}
            {vesselInfo?.name ? ` · ${vesselInfo.name}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {isDraft && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/dashboard/admin/rfqs/new?draftId=${rfqDetails._id}&returnTo=purchases`
                )
              }
              className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-amber-800 shadow-sm transition-all hover:bg-amber-100"
            >
              <MdEdit className="h-4 w-4" />
              Continue Editing
            </button>
          )}
        </div>
      </div>

      <ProcurementWorkflowStepper
        currentStatus={procurementWorkflowStatus}
        context={procurementWorkflowContext}
        lastUpdated={rfqDetails.updatedAt || rfqDetails.createdAt}
      />

      {/* Overview cards — customer parity */}
      <div
        className={cn(
          'rounded-2xl border p-8 shadow-sm',
          isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-white'
        )}
      >
        <h2 className="mb-6 flex items-center gap-2 border-b border-[#0088cc]/15 pb-4 text-sm font-bold uppercase tracking-widest text-[hsl(var(--foreground))]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#0088cc]" />
          Operational Snapshot
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Initialization Date
            </label>
            <p className="text-sm font-bold">
              {formatServerDate(rfqDetails.createdAt)} UTC
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Operational Deadline
            </label>
            <p className="text-sm font-bold">
              {rfqDetails.dueDate || rfqDetails.leadDate
                ? new Date(rfqDetails.dueDate || rfqDetails.leadDate!).toLocaleDateString()
                : '—'}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Designated Port
            </label>
            <p className="text-sm font-bold">{rfqDetails.supplyPort || '—'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Resource Category
            </label>
            <p className="text-sm font-bold capitalize">{rfqDetails.category || '—'}</p>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'rounded-2xl border p-8 shadow-sm',
          isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-white'
        )}
      >
        <h2 className="mb-6 flex items-center gap-2 border-b border-[#0088cc]/15 pb-4 text-sm font-bold uppercase tracking-widest">
          <span className="h-1.5 w-1.5 rounded-full bg-[#0088cc]" />
          Vessel & Equipment
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Vessel
            </label>
            <p className="text-sm font-bold">{vesselInfo?.name || '—'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              IMO
            </label>
            <p className="text-sm font-bold">{vesselInfo?.imoNumber || '—'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Brand
            </label>
            <p className="text-sm font-bold">{rfqDetails.brand || '—'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
              Model
            </label>
            <p className="text-sm font-bold">{rfqDetails.model || '—'}</p>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div
        className={cn(
          'rounded-2xl border p-8 shadow-sm',
          isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-white'
        )}
      >
        <h2 className="mb-6 flex items-center gap-2 border-b border-[#0088cc]/15 pb-4 text-sm font-bold uppercase tracking-widest">
          <span className="h-1.5 w-1.5 rounded-full bg-[#0088cc]" />
          Line Items
        </h2>
        <div className={erpTableShellClass}>
          <div className={erpTableScrollClass}>
            <table className={erpTableClass}>
              <thead>
                <tr className={erpTableHeadRowClass}>
                  <th className={erpTableHeadCellClass}>Description</th>
                  <th className={erpTableHeadCellClass}>Part No</th>
                  <th className={cn(erpTableHeadCellClass, 'text-center')}>Qty</th>
                  <th className={erpTableHeadCellClass}>UOM</th>
                  <th className={erpTableHeadCellClass}>Remark</th>
                </tr>
              </thead>
              <tbody>
                {rfqItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-sm text-[hsl(var(--muted-foreground))]"
                    >
                      No line items on this RFQ
                    </td>
                  </tr>
                ) : (
                  rfqItems.map((item, idx) => (
                    <tr key={idx} className={erpTableRowClasses()}>
                      <td className="px-6 py-4 text-sm font-semibold">{item.description}</td>
                      <td className="px-6 py-4 text-sm">{item.partNo}</td>
                      <td className="px-6 py-4 text-center text-sm font-bold">
                        {item.requiredQty}
                      </td>
                      <td className="px-6 py-4 text-sm">{item.uom}</td>
                      <td className="px-6 py-4 text-sm text-[hsl(var(--muted-foreground))]">
                        {item.remark}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Vendor quotations */}
      <div
        className={cn(
          'rounded-2xl border p-8 shadow-sm',
          isDark ? 'border-white/[0.08] bg-[#0f172a]' : 'border-[#0088cc]/20 bg-white'
        )}
      >
        <h2 className="mb-6 flex items-center gap-2 border-b border-[#0088cc]/15 pb-4 text-sm font-bold uppercase tracking-widest">
          <span className="h-1.5 w-1.5 rounded-full bg-[#0088cc]" />
          Vendor Quotations
        </h2>

        {vendorQuotations.length === 0 ? (
          <p className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">
            No quotations received yet. Invited vendors will appear here when they submit offers.
          </p>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap gap-2 border-b border-[#0088cc]/15">
              {vendorQuotations.map((q, tabIndex) => {
                const isFinalized = q.status === 'finalized';
                return (
                  <button
                    key={q._id}
                    type="button"
                    onClick={() => setSelectedVendorTab(tabIndex)}
                    className={cn(
                      'relative flex items-center gap-2 px-6 py-4 text-xs font-bold uppercase tracking-widest transition-all',
                      selectedVendorTab === tabIndex
                        ? 'text-[#0088cc]'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                    )}
                  >
                    {q.vendorOrganizationId?.name ||
                      (q.isAdminOffer ? EUROASIANN_OFFER_LABEL : 'Vendor')}
                    {isFinalized && (
                      <span className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-extrabold uppercase text-emerald-700">
                        Awarded
                      </span>
                    )}
                    {selectedVendorTab === tabIndex && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0088cc]" />
                    )}
                  </button>
                );
              })}
            </div>

            {vendorQuotations[selectedVendorTab] &&
              (() => {
                const quotation = vendorQuotations[selectedVendorTab];
                const isFinalized = quotation.status === 'finalized';
                const selected =
                  selectedItemsByQuotation[quotation._id] ??
                  quotation.selectedItemIndexes ??
                  quotation.items.map((_, i) => i);
                const currency =
                  quotation.terms?.currency || quotation.currency || 'USD';

                return (
                  <div className="space-y-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0088cc]">
                          {quotation.quotationNumber}
                        </p>
                        <p className="mt-1 text-sm font-bold">
                          {quotation.vendorOrganizationId?.name ||
                            (quotation.isAdminOffer ? EUROASIANN_OFFER_LABEL : 'Vendor')}
                        </p>
                      </div>
                      <span
                        className={cn(
                          'rounded-full border px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-widest',
                          isFinalized
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-[#0088cc]/20 bg-[#0088cc]/5 text-[hsl(var(--muted-foreground))]'
                        )}
                      >
                        {toDisplayStatus(quotation.status)}
                      </span>
                    </div>

                    <div className={erpTableShellClass}>
                      <div className={erpTableScrollClass}>
                        <table className={erpTableClass}>
                          <thead>
                            <tr className={erpTableHeadRowClass}>
                              {!isFinalized && !hasAnyFinalizedOffer && (
                                <th className={cn(erpTableHeadCellClass, 'w-12 text-center')}>
                                  Sel
                                </th>
                              )}
                              <th className={erpTableHeadCellClass}>Item</th>
                              <th className={cn(erpTableHeadCellClass, 'text-center')}>
                                Offered Qty
                              </th>
                              <th className={cn(erpTableHeadCellClass, 'text-right')}>
                                Unit Price
                              </th>
                              <th className={erpTableHeadCellClass}>Quality</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotation.items.map((item, itemIdx) => {
                              const checked = selected.includes(itemIdx);
                              return (
                                <tr key={itemIdx} className={erpTableRowClasses()}>
                                  {!isFinalized && !hasAnyFinalizedOffer && (
                                    <td className="px-4 py-3 text-center">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => {
                                          setSelectedItemsByQuotation((prev) => {
                                            const current =
                                              prev[quotation._id] ??
                                              quotation.items.map((_, i) => i);
                                            const next = current.includes(itemIdx)
                                              ? current.filter((i) => i !== itemIdx)
                                              : [...current, itemIdx].sort((a, b) => a - b);
                                            return { ...prev, [quotation._id]: next };
                                          });
                                        }}
                                      />
                                    </td>
                                  )}
                                  <td className="px-6 py-3 text-sm font-semibold">
                                    {item.itemDescription || item.description}
                                  </td>
                                  <td className="px-6 py-3 text-center text-sm">
                                    {item.offeredQty}
                                  </td>
                                  <td className="px-6 py-3 text-right text-sm font-bold">
                                    {currency}{' '}
                                    {Number(
                                      item.quotedPrice ?? item.unitPrice ?? 0
                                    ).toFixed(2)}
                                  </td>
                                  <td className="px-6 py-3 text-sm">
                                    {item.offeredQuality || '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {quotation.terms && (
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        {quotation.terms.paymentTerm && (
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
                              Payment
                            </label>
                            <p className="text-sm font-semibold">
                              {quotation.terms.paymentTerm}
                            </p>
                          </div>
                        )}
                        {quotation.terms.deliveryTerm && (
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
                              Delivery Term
                            </label>
                            <p className="text-sm font-semibold">
                              {quotation.terms.deliveryTerm}
                            </p>
                          </div>
                        )}
                        {quotation.terms.transportTerm && (
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
                              Transport
                            </label>
                            <p className="text-sm font-semibold">
                              {quotation.terms.transportTerm}
                            </p>
                          </div>
                        )}
                        {quotation.terms.currency && (
                          <div>
                            <label className="text-[10px] font-bold uppercase tracking-widest text-[#0088cc]/65">
                              Currency
                            </label>
                            <p className="text-sm font-semibold">
                              {quotation.terms.currency}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {!isFinalized && !hasAnyFinalizedOffer && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          disabled={
                            awardMutation.isPending || selected.length === 0
                          }
                          onClick={() =>
                            awardMutation.mutate({
                              quotationId: quotation._id,
                              selectedItemIndexes: selected,
                            })
                          }
                          className="rounded-xl bg-emerald-600 px-8 py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-md transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {awardMutation.isPending
                            ? 'Awarding…'
                            : 'Finalize / Award Offer'}
                        </button>
                      </div>
                    )}

                    {isFinalized && (
                      <div className="space-y-6">
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 shadow-sm">
                          <div className="flex flex-col items-center gap-4 md:flex-row md:text-left">
                            <MdCheckCircle className="h-8 w-8 text-emerald-600" />
                            <div>
                              <p className="text-sm font-extrabold uppercase tracking-widest text-emerald-800">
                                Directive Awarded
                              </p>
                              <p className="mt-1 text-xs font-semibold text-emerald-700">
                                This procurement cycle has been successfully finalized.
                              </p>
                            </div>
                          </div>
                        </div>

                        {po && (
                          <div className="rounded-2xl border border-[#0088cc]/20 bg-[#0088cc]/5 p-8 shadow-sm">
                            <div className="flex flex-col gap-6">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <MdShoppingCart className="h-6 w-6 text-[#0088cc]" />
                                  <span className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--foreground))]">
                                    Documented Purchase Order
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/dashboard/admin/pos/${po._id}`)
                                  }
                                  className="rounded-full border border-[#0088cc]/20 bg-white px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-[hsl(var(--foreground))] shadow-sm hover:border-[#0088cc]"
                                >
                                  {po.poNumber}
                                </button>
                              </div>
                              <div className="flex items-center justify-between border-t border-[#0088cc]/20/60 pt-6">
                                <div className="flex items-center gap-3">
                                  <MdPayment className="h-5 w-5 text-[#0088cc]/65" />
                                  <span className="text-sm font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                                    Financial Status
                                  </span>
                                </div>
                                <span
                                  className={cn(
                                    'rounded-full border px-4 py-1.5 text-[10px] font-extrabold uppercase shadow-sm',
                                    (po.paymentStatus || '').toLowerCase() === 'paid'
                                      ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                                      : 'border-amber-100 bg-amber-50 text-amber-700'
                                  )}
                                >
                                  {po.paymentStatus || 'Awaiting Settlement'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
          </>
        )}
      </div>

      {isOngoingOrProcessed && hasAnyFinalizedOffer && po?.status === 'issued' && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center space-y-4 rounded-xl border border-amber-100 bg-amber-50 p-8 text-center">
            <div className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full border border-amber-200 bg-white shadow-sm">
              <MdShoppingCart className="h-8 w-8 text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-black uppercase tracking-tight text-amber-900">
                Order Confirmation Pending
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm font-bold leading-relaxed text-amber-700/80">
                Purchase Order{' '}
                <span className="font-black text-amber-900">#{po.poNumber}</span> has been
                issued. After the vendor acknowledges, you can review invoices, choose shipping,
                pay, and track the shipment.
              </p>
            </div>
          </div>
        </div>
      )}

        {/* Invoices (proforma) → Shipping → Final Invoice → Payment */}
  {hasAnyFinalizedOffer && po && po.status !== 'issued' && poId && (
    <div className="mt-6 space-y-6">
      <AdminBuyerInvoicesSection poId={String(poId)} po={po} variant="proforma" />
      <AdminBuyerShippingSection
        po={po}
        poId={String(poId)}
        canConfigure={!['cancelled', 'completed'].includes(String(po.status || ''))}
      />
      <AdminBuyerInvoicesSection poId={String(poId)} po={po} variant="final" />
      <AdminBuyerPaymentSection poId={String(poId)} po={po} />
    </div>
  )}

      {/* Shipment Tracking → Delivery Status */}
      {isOngoingOrProcessed && showDeliveryStatus && (
        <RfqDeliveryStatusSection purchaseOrder={po} shipment={linkedShipment} />
      )}
    </div>
  );
}
